import type { ChatReadyMessage } from "@albertine/types";
import { ChatMessageSchema } from "@albertine/types/openapi/concierge-v1";
import { isEmbeddedNativeWebView } from "@albertine/wrapper-interop";
import type { ConnectionAdapter } from "@tanstack/ai-react";
import { useChat } from "@tanstack/ai-react";
import { useEffect, useEffectEvent } from "react";
import { usesDevMemberLogin } from "@/lib/devMemberLogin/devMemberLoginEnabled";
import { getKeycloakToken } from "@/lib/getKeycloakToken";
import { notifyParentUnauthorized } from "@/lib/iframeKeycloakToken";
import {
    isEmbeddedInParentFrame,
    parentOriginFromLocation,
    postMessageToParentFrame,
} from "@/lib/parentFrame";
import { type ChatScope, getChatEventsUrlSafe } from "../-data/chatApi";
import {
    ConciergeChatEventNameSchema,
    createConciergeSseConnection,
} from "../-data/conciergeSseConnection";
import { applyConciergeEvent } from "../-utils/applyConciergeEvent";
import type { ChatThreadPage } from "../-utils/threadCache";

const idleConnection: ConnectionAdapter = {
    subscribe: () => emptySubscription(),
    send: () => Promise.resolve(),
};

export function useChatLiveEvents({
    scope,
    isPending,
    updateThread,
}: {
    scope: ChatScope;
    isPending: boolean;
    updateThread: (
        update: (page: ChatThreadPage | undefined) => ChatThreadPage,
    ) => void;
}) {
    const eventStreamUrl =
        isEmbeddedInParentFrame() ||
        usesDevMemberLogin() ||
        isEmbeddedNativeWebView()
            ? getChatEventsUrlSafe(scope)
            : null;

    const connection = eventStreamUrl
        ? createConciergeSseConnection({
              url: eventStreamUrl,
              getAccessToken: getKeycloakToken,
              onUnauthorized: notifyParentUnauthorized,
          })
        : idleConnection;

    const handleCustomEvent = useEffectEvent((eventType: string, value: unknown) => {
        const name = ConciergeChatEventNameSchema.safeParse(eventType);

        if (!name.success) {
            return;
        }

        applyConciergeEvent(name.data, value, updateThread);
    });

    const { error: chatError } = useChat({
        connection,
        initialMessages: [],
        live: eventStreamUrl !== null,
        onCustomEvent: handleCustomEvent,
        outputSchema: ChatMessageSchema,
    });

    useEffect(
        function notifyParentReady() {
            if (!isEmbeddedInParentFrame() || isPending) {
                return;
            }

            const readyMessage: ChatReadyMessage = { type: "chat.ready" };
            postMessageToParentFrame(parentOriginFromLocation(), readyMessage);
        },
        [isPending],
    );

    return { chatError };
}

async function* emptySubscription(): AsyncIterable<never> {}
